import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "../context/GameContext.jsx";
import { useLanguage } from "../hooks/useLanguage.js";
import { fadeUp } from "../animations/variants.js";
import { Btn } from "../components/ui/Btn.jsx";
import { Panel, SectionTitle } from "../components/ui/Panel.jsx";
import { LangToggle } from "../components/ui/LangToggle.jsx";
import { ScrollNav } from "../components/ui/ScrollNav.jsx";
import { DraggablePlayerList } from "../components/ui/DraggablePlayerList.jsx";
import { SettingsSheet } from "../components/settings/SettingsSheet.jsx";
import { RoleBadge } from "../components/role/RoleBadge.jsx";
import { ROLE_DATA, sortedByRoleBadge } from "../data/roles.js";
import { assignRoles, buildGameSession } from "../engine/index.js";

// Camp groupings — sorted so "new" roles appear first, "experimental" last
const VILLAGE_SPECIALS = sortedByRoleBadge(["witch","protector","hunter","cupid","seer","raven","bear_tamer","little_girl","elder","fox","shepherd"]);
const WOLF_SPECIALS    = sortedByRoleBadge(["father_of_wolves","big_bad_wolf"]);
const SOLO_SPECIALS    = sortedByRoleBadge(["flute_player","white_werewolf"]);
const MIXED_SPECIALS   = sortedByRoleBadge(["angel","wild_child","thief"]);

function CampLabel({ label, color }) {
  return <p className={`text-[10px] uppercase tracking-widest mb-2 mt-4 first:mt-0 ${color}`}>{label}</p>;
}

export function SetupScreen() {
  const { state, dispatch } = useGame();
  const { t, lang } = useLanguage();
  const rc = state.roleConfig;
  const [showSettings, setShowSettings] = useState(false);
  const scrollRef = useRef(null);

  const activePlayers = state.savedPlayers.filter(p => state.activePlayerIds.includes(p.id));
  const N = activePlayers.length;

  const specialCount = rc.specialRoles.length;
  const totalRoles = rc.villagerCount + rc.werewolfCount + specialCount;
  const hasWolfSide = rc.werewolfCount > 0 || rc.specialRoles.some(r => ROLE_DATA[r]?.alignment === "werewolf");
  const hasVillageSide = rc.villagerCount > 0 || rc.specialRoles.some(r => ROLE_DATA[r]?.alignment === "village");
  const wolfCampCount = rc.werewolfCount
    + rc.specialRoles.filter(r => ROLE_DATA[r]?.alignment === "werewolf" && r !== "white_werewolf").length;
  const villageCampCount = rc.villagerCount
    + rc.specialRoles.filter(r => ROLE_DATA[r]?.alignment === "village").length;
  const alreadyDecided = hasWolfSide && hasVillageSide && wolfCampCount >= villageCampCount;
  const isValid = N >= 4 && totalRoles === N && hasWolfSide && hasVillageSide && !alreadyDecided;

  const handleStart = () => {
    if (!isValid) return;
    const assignments = assignRoles(activePlayers, rc);
    const session = buildGameSession(activePlayers, assignments, rc, state.settings);
    const queue = [...assignments.map(a => ({ playerId: a.playerId, roleId: a.roleId }))]
      .sort(() => Math.random() - 0.5);
    dispatch({ type: "START_REVEAL", payload: { queue, session } });
  };

  const RoleGrid = ({ roleIds }) => (
    <div className="grid grid-cols-2 gap-2 mb-1">
      {roleIds.map(roleId => {
        const role = ROLE_DATA[roleId];
        if (!role) return null;
        const active = rc.specialRoles.includes(roleId);
        return (
          <motion.button key={roleId} whileTap={{ scale: 0.97 }}
            onClick={() => dispatch({ type: "TOGGLE_SPECIAL_ROLE", payload: roleId })}
            className="relative rounded-2xl border text-left transition-all overflow-hidden aspect-[3/4] w-full">
            <img src={role.imageUrl} className="absolute inset-0 w-full h-full aspect-[3/4] object-cover" />
            <div className={`absolute inset-0 bg-gradient-to-t ${active ? "from-amber-950 via-amber-950/20" : "from-black via-black/20"} to-transparent`} />
            {role.roleBadge && (
              <div className="absolute top-1.5 right-1.5 z-10">
                <RoleBadge roleBadge={role.roleBadge} lang={lang} />
              </div>
            )}
            <div className="relative z-10 p-3 flex flex-col justify-end h-full">
              <p className="text-xs font-semibold text-white">{t(role.frenchName, role.englishName)}</p>
              {active && <span className="text-white/50 text-[9px]">✓ {t("Actif", "Active")}</span>}
            </div>
          </motion.button>
        );
      })}
    </div>
  );

  return (
    <motion.div variants={fadeUp} initial="initial" animate="animate" exit="exit"
      className="min-h-screen bg-gradient-to-b from-stone-950 via-amber-950 to-stone-950 flex flex-col">

      <div className="px-4 pt-10 pb-4 flex items-center gap-3">
        <button onClick={() => dispatch({ type: "GO_TO_HOME" })}
          className="text-amber-600 text-xl w-9 h-9 rounded-xl bg-amber-950/50 border border-amber-800/30 flex items-center justify-center active:scale-95">←</button>
        <div className="flex-1">
          <h1 className="text-amber-200 font-bold text-lg">{t("Préparer la partie", "Set up game")}</h1>
          <p className="text-amber-700/50 text-xs">{N} {t("joueurs actifs", "active players")}</p>
        </div>
        <button onClick={() => setShowSettings(true)}
          className="bg-amber-950/60 border border-amber-800/40 rounded-full px-3 py-1.5 text-amber-400 text-xs font-semibold active:scale-95 transition-all">⚙️</button>
        <LangToggle />
      </div>

      <div ref={scrollRef} className="flex-1 px-4 overflow-y-auto pb-36">

        <Panel className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <SectionTitle>{t("Joueurs actifs", "Active players")}</SectionTitle>
            <div className="flex gap-1.5">
              <button onClick={() => dispatch({ type: "SELECT_ALL_PLAYERS" })}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-xl bg-amber-900/50 border border-amber-700/40 text-amber-300 active:scale-95 transition-all">{t("Tous ✓", "All ✓")}</button>
              <button onClick={() => dispatch({ type: "DESELECT_ALL_PLAYERS" })}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-xl bg-stone-900/60 border border-stone-700/30 text-stone-400 active:scale-95 transition-all">{t("Aucun ✕", "None ✕")}</button>
            </div>
          </div>
          <p className="text-amber-600/60 text-xs mb-3">
            {t("Sélectionnez les joueurs qui participent à cette partie.", "Select the players participating in this game.")}
          </p>
          <DraggablePlayerList players={state.savedPlayers} renderRow={p => (
            <motion.button whileTap={{ scale: 0.97 }}
              onClick={() => dispatch({ type: "TOGGLE_PLAYER_ACTIVE", payload: p.id })}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-all
                ${state.activePlayerIds.includes(p.id) ? "border-amber-500/60 bg-amber-900/30" : "border-amber-900/20 bg-stone-950/30"}`}>
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0
                ${state.activePlayerIds.includes(p.id) ? "border-amber-400 bg-amber-500" : "border-amber-800/40"}`}>
                {state.activePlayerIds.includes(p.id) && <span className="text-amber-950 text-xs font-bold">✓</span>}
              </div>
              <span className="text-amber-100 text-sm">{p.name}</span>
            </motion.button>
          )} />
        </Panel>

        <Panel className="mb-4">
          <SectionTitle>{t("Composition de base", "Base composition")}</SectionTitle>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-emerald-400 text-sm">🏡 {t("Villageois", "Villagers")}</span>
              <span className="text-emerald-300 font-bold text-lg">{rc.villagerCount}</span>
            </div>
            <input type="range" min={0} max={N} value={rc.villagerCount}
              onChange={e => {
                const next = +e.target.value;
                const isDecrease = next < rc.villagerCount;
                if (isDecrease || next + rc.werewolfCount + specialCount <= N) {
                  dispatch({ type: "SET_VILLAGER_COUNT", payload: next });
                }
              }}
              className="w-full accent-emerald-500" />
          </div>

          <div className="mb-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-red-400 text-sm">🐺 {t("Loups-Garous", "Werewolves")}</span>
              <span className="text-red-300 font-bold text-lg">{rc.werewolfCount}</span>
            </div>
            <input type="range" min={0} max={N} value={rc.werewolfCount}
              onChange={e => {
                const next = +e.target.value;
                const isDecrease = next < rc.werewolfCount;
                if (isDecrease || next + rc.villagerCount + specialCount <= N) {
                  dispatch({ type: "SET_WEREWOLF_COUNT", payload: next });
                }
              }}
              className="w-full accent-red-500" />
          </div>

          {(() => {
            const assigned = rc.villagerCount + rc.werewolfCount + specialCount;
            const remaining = N - assigned;
            const balanceWarn = !hasWolfSide || !hasVillageSide;
            const overBudget = assigned > N;
            const underBudget = assigned < N;
            return (
              <div className="space-y-2 mt-3">
                <div className={`rounded-xl px-3 py-2 text-center text-xs font-semibold
                  ${overBudget ? "bg-red-950/50 border border-red-800/40 text-red-400"
                    : underBudget ? "bg-amber-950/50 border border-amber-800/40 text-amber-400"
                    : "bg-emerald-950/40 border border-emerald-800/30 text-emerald-400"}`}>
                  {assigned} / {N} {t("rôles attribués", "roles assigned")}
                  {overBudget && ` — ${t("trop de rôles !", "too many roles!")}`}
                  {underBudget && remaining > 0 && ` — ${remaining} ${t("à attribuer", "unassigned")}`}
                </div>
                {balanceWarn && N >= 4 && (
                  <div className="rounded-xl px-3 py-2 text-center text-xs bg-orange-950/40 border border-orange-800/30 text-orange-400">
                    {!hasWolfSide ? t("⚠️ Aucun loup dans la partie !", "⚠️ No wolves in the game!")
                      : t("⚠️ Aucun villageois dans la partie !", "⚠️ No villagers in the game!")}
                  </div>
                )}
                {!balanceWarn && alreadyDecided && N >= 4 && assigned === N && (
                  <div className="rounded-xl px-3 py-2 text-center text-xs bg-red-950/50 border border-red-800/40 text-red-400">
                    {t(`⚖️ Déséquilibré : ${wolfCampCount} loup(s) vs ${villageCampCount} villageois. Les loups gagnent déjà.`,
                       `⚖️ Unbalanced: ${wolfCampCount} wolf(ves) vs ${villageCampCount} villager(s). Wolves already win.`)}
                  </div>
                )}
              </div>
            );
          })()}
        </Panel>

        <Panel className="mb-4">
          <SectionTitle>{t("Rôles Spéciaux", "Special Roles")}</SectionTitle>
          <p className="text-amber-600/50 text-xs mb-3">
            {t("Chaque rôle ajouté remplace un rôle de base.", "Each added role replaces a base role.")}
          </p>

          <CampLabel label={t("Camp Village", "Village side")} color="text-emerald-600/60" />
          <RoleGrid roleIds={VILLAGE_SPECIALS} />

          <CampLabel label={t("Camp Loups", "Wolf side")} color="text-red-600/60" />
          <RoleGrid roleIds={WOLF_SPECIALS} />

          <CampLabel label={t("Solo / Indépendant", "Solo / Independent")} color="text-teal-600/60" />
          <RoleGrid roleIds={SOLO_SPECIALS} />

          <CampLabel label={t("Mixte / Conditionnel", "Mixed / Conditional")} color="text-amber-500/60" />
          <p className="text-amber-700/40 text-[10px] mb-2 italic">
            {t("Alignement ou victoire peut changer selon le déroulement de la partie.", "Alignment or victory may change based on events in the game.")}
          </p>
          <RoleGrid roleIds={MIXED_SPECIALS} />
        </Panel>
      </div>

      <div className="fixed bottom-0 left-0 right-0 px-4 pb-8 pt-4 bg-gradient-to-t from-stone-950 via-stone-950/90 to-transparent">
        {!isValid && N < 4 && (
          <p className="text-amber-800/60 text-xs text-center mb-2">{t("Sélectionnez au moins 4 joueurs", "Select at least 4 players")}</p>
        )}
        <Btn variant="primary" onClick={handleStart} disabled={!isValid} size="lg" className="w-full">
          {t("🎭 Distribuer les rôles", "🎭 Distribute roles")}
        </Btn>
      </div>

      <ScrollNav scrollRef={scrollRef} />
      <AnimatePresence>
        {showSettings && <SettingsSheet onClose={() => setShowSettings(false)} />}
      </AnimatePresence>
    </motion.div>
  );
}
